import { useCallback } from "react";
import { format } from "date-fns";

import {
  BOOKING_FLOW_EVENTS,
  BOOKING_PAGE_VIEWS,
  BookingFlowEvent,
  InterstitialVariant,
  Provider,
  useStoreSlotDateViewMutation,
  useUpdateBookingFlowMutation,
} from "~/services";
import { TimeSlot } from "~/shared.types";
import { formatDateKey, formatDateTimeForAPI } from "~/shared/utils";
import { useBookingFlowStore } from "~/stores";

export const useBookingFlowEvents = () => {
  const { mutate: updateBookingFlow } = useUpdateBookingFlowMutation();
  const { mutate: storeSlotDateView } = useStoreSlotDateViewMutation();
  const { flow: bookingFlow } = useBookingFlowStore();

  const trackSchedulingEntryPage = () => {
    if (!bookingFlow) return;
    const { isProviderView } = useBookingFlowStore.getState();
    const now = new Date();
    updateBookingFlow({
      id: bookingFlow.id,
      [BOOKING_FLOW_EVENTS.SCHEDULING_PAGE_VIEWED_AT]: now.toISOString(),
      [BOOKING_FLOW_EVENTS.SCHEDULE_ENTRY_PAGE]: isProviderView
        ? BOOKING_PAGE_VIEWS.PROVIDER_VIEW
        : BOOKING_PAGE_VIEWS.FIRST_AVAILABLE_VIEW,
    });
  };

  const trackTimeEvent = useCallback(
    (event: BookingFlowEvent, bookingFlowId?: string) => {
      if (!bookingFlowId && !bookingFlow?.id) return;
      const now = new Date();
      updateBookingFlow({
        id: bookingFlowId ?? bookingFlow!.id,
        [event]: now.toISOString(),
      });
    },
    [updateBookingFlow, bookingFlow?.id],
  );

  const trackSelectedSlot = (
    slot: TimeSlot,
    date: string,
    providerId?: number,
  ) => {
    if (!bookingFlow || !slot.originalTime || !providerId) return;

    const formattedDateTime = formatDateTimeForAPI(date, slot.originalTime);

    updateBookingFlow({
      id: bookingFlow.id,
      [BOOKING_FLOW_EVENTS.SELECTED_APPOINTMENT_SLOT]: formattedDateTime,
      [BOOKING_FLOW_EVENTS.PROVIDER_ID]: providerId,
      [BOOKING_FLOW_EVENTS.APPOINTMENT_SLOT_SUBMITTED_AT]:
        new Date().toISOString(),
      [BOOKING_FLOW_EVENTS.SCHEDULE_CONTINUATION_PAGE]:
        useBookingFlowStore.getState().isProviderView
          ? BOOKING_PAGE_VIEWS.PROVIDER_VIEW
          : BOOKING_PAGE_VIEWS.FIRST_AVAILABLE_VIEW,
    });
  };

  const trackEvent = (
    event: BookingFlowEvent,
    value: boolean | number | string,
  ) => {
    if (!bookingFlow) return;
    updateBookingFlow({
      id: bookingFlow.id,
      [event]: value,
    });
  };
  const trackOnStateChangeAndResetInsurance = ({
    isOtherState,
    stateId,
  }: {
    isOtherState: boolean;
    stateId: number | null;
  }) => {
    if (!bookingFlow) return;
    updateBookingFlow({
      id: bookingFlow.id,
      [BOOKING_FLOW_EVENTS.IS_OTHER_STATE]: isOtherState,
      [BOOKING_FLOW_EVENTS.STATE_ID]: stateId,
      [BOOKING_FLOW_EVENTS.IS_NOT_LISTED_INSURANCE]: false,
      [BOOKING_FLOW_EVENTS.INSURANCE_ID]: null,
    });
  };

  const trackInterstitialVariant = (
    variant: InterstitialVariant,
    bookingFlowId: string,
  ) => {
    updateBookingFlow({
      id: bookingFlowId,
      [BOOKING_FLOW_EVENTS.INTERSTITIAL_VARIANT]: variant,
    });
  };

  const trackInterstitialGoalsSubmit = (goals: string[]) => {
    if (!bookingFlow) return;
    updateBookingFlow({
      id: bookingFlow.id,
      [BOOKING_FLOW_EVENTS.INTERSTITIAL_GOAL_SELECTIONS]: goals,
      [BOOKING_FLOW_EVENTS.INTERSTITIAL_QUESTION_PAGE_SUBMITTED_AT]:
        new Date().toISOString(),
    });
  };

  const trackSlotDateView = (
    date: Date,
    providers: Provider[],
    onSuccess?: () => void,
  ) => {
    if (!bookingFlow) return;
    const dateKey = formatDateKey(date);
    const availableProviders = providers
      .map((provider) => ({
        id: provider.id,
        slotCount: provider.slots[dateKey]?.length ?? 0,
      }))
      .filter((provider) => provider.slotCount > 0);

    if (!availableProviders.length) return;

    storeSlotDateView(
      {
        id: bookingFlow.id,
        slotDateViewed: format(date, "yyyy-MM-dd"),
        providers: availableProviders,
      },
      { onSuccess },
    );
  };

  return {
    trackSchedulingEntryPage,
    trackEvent,
    trackOnStateChangeAndResetInsurance,
    trackTimeEvent,
    trackSelectedSlot,
    trackSlotDateView,
    trackInterstitialVariant,
    trackInterstitialGoalsSubmit,
  };
};
